/**
 * 多语言字段辅助工具
 * 根据当前语言环境自动获取对应的多语言字段
 */

/**
 * 获取多语言字段值
 * @param {Object} obj - 包含多语言字段的对象
 * @param {String} fieldName - 字段名称（不含语言后缀）
 * @param {String} locale - 当前语言 ('zh', 'en', 'es')
 * @param {String} suffix - 字段后缀类型 ('Cn/En/Es' 或 'Zh/En/Es')
 * @returns {String} 对应语言的字段值
 */
export function getI18nField(obj, fieldName, locale, suffix = 'standard') {
  if (!obj) return '';

  // 处理特殊情况：TopImgsEntity 使用 Zh 而非 Cn
  const cnSuffix = suffix === 'zh' ? 'Zh' : 'Cn';

  // 根据语言返回对应字段
  if (locale === 'zh') {
    return obj[`${fieldName}${cnSuffix}`] || obj[`${fieldName}En`] || '';
  } else if (locale === 'es') {
    return obj[`${fieldName}Es`] || obj[`${fieldName}En`] || '';
  } else {
    return obj[`${fieldName}En`] || '';
  }
}

/**
 * 批量转换对象的多语言字段
 * @param {Object} obj - 原始对象
 * @param {Array} fields - 需要转换的字段名数组
 * @param {String} locale - 当前语言
 * @param {String} suffix - 字段后缀类型
 * @returns {Object} 转换后的对象
 */
export function transformI18nFields(obj, fields, locale, suffix = 'standard') {
  if (!obj) return {};

  const result = { ...obj };

  fields.forEach(field => {
    result[field] = getI18nField(obj, field, locale, suffix);
  });

  return result;
}

/**
 * 转换列表数据的多语言字段
 * @param {Array} list - 数据列表
 * @param {Array} fields - 需要转换的字段名数组
 * @param {String} locale - 当前语言
 * @param {String} suffix - 字段后缀类型
 * @returns {Array} 转换后的列表
 */
export function transformI18nList(list, fields, locale, suffix = 'standard') {
  if (!Array.isArray(list)) return [];

  return list.map(item => transformI18nFields(item, fields, locale, suffix));
}

/**
 * Vue mixin - 提供多语言字段获取方法
 */
export const i18nFieldMixin = {
  methods: {
    /**
     * 获取多语言字段
     * @param {Object} obj - 对象
     * @param {String} fieldName - 字段名
     * @param {String} suffix - 后缀类型 ('standard' 或 'zh')
     */
    $getI18nField(obj, fieldName, suffix = 'standard') {
      return getI18nField(obj, fieldName, this.$i18n.locale, suffix);
    },

    /**
     * 批量转换多语言字段
     * @param {Object} obj - 对象
     * @param {Array} fields - 字段名数组
     * @param {String} suffix - 后缀类型
     */
    $transformI18nFields(obj, fields, suffix = 'standard') {
      return transformI18nFields(obj, fields, this.$i18n.locale, suffix);
    },

    /**
     * 转换列表的多语言字段
     * @param {Array} list - 列表
     * @param {Array} fields - 字段名数组
     * @param {String} suffix - 后缀类型
     */
    $transformI18nList(list, fields, suffix = 'standard') {
      return transformI18nList(list, fields, this.$i18n.locale, suffix);
    }
  }
};
